// Structured error helpers. Companion to the backend SSE + error overlays
// in fmc-deploy/. Normalises any axios error into a consistent shape and
// exposes severity hints for the ErrorDialog component.

import { useState, useCallback } from "react";

/**
 * Convert an axios error (or anything that looks like one) into a
 * consistent envelope. Works whether the backend returned a structured
 * envelope (status/error_kind/message/hint/actions/details) or not.
 */
export function normaliseApiError(err) {
  const status = err?.response?.status ?? 0;
  const body = err?.response?.data ?? {};
  const isStructured = !!body.error_kind;

  return {
    status,
    isStructured,
    error_kind: body.error_kind ?? (status >= 500 ? "UNHANDLED" : "UNKNOWN"),
    message: body.message ?? err?.message ?? "An unknown error occurred.",
    hint: body.hint ?? null,
    actions: Array.isArray(body.actions) ? body.actions : [],
    details: body.details ?? null,
    raw: err,
  };
}

/** Map error_kind → MUI Alert severity for the dialog. */
export function severityFor(kind) {
  switch (kind) {
    case "OBSERVATION_ON_TARGET":
    case "OBSERVATION_AT_BATTERY":
    case "NO_PRIOR_SHOT":
      return "warning"; // user-correctable input issue
    case "API_ERROR":
    case "UNHANDLED":
    default:
      return "error";
  }
}

/**
 * Local error state hook. Accepts an axios error or a pre-normalised envelope.
 * Most callers should use useError() from ErrorContext instead — this hook
 * is for components that want page-local error state.
 */
export function useStructuredError() {
  const [error, _setError] = useState(null);

  const setError = useCallback((e) => {
    if (e && typeof e === "object" && "isStructured" in e) {
      _setError(e);
    } else {
      _setError(normaliseApiError(e));
    }
  }, []);

  const clear = useCallback(() => _setError(null), []);

  return { error, setError, clear };
}
